import { io } from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.listeners = new Map();
  }

  connect(token) {
    if (this.socket && this.connected) {
      return this.socket;
    }

    if (!token) {
      console.error('No authentication token provided for socket');
      return null;
    }

    const socketUrl = process.env.NODE_ENV === 'production'
      ? 'https://ugc4you.nl'
      : 'http://localhost:5000';

    this.socket = io(socketUrl, {
      auth: {
        token
      },
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      timeout: 20000,
      forceNew: true,
    });

    this.setupEventHandlers();
    return this.socket;
  }

  setupEventHandlers() {
    this.socket.on('connect', () => {
      console.log('Connected to WebSocket server');
      this.connected = true;
      this.notifyListeners('connection', { connected: true });
    });

    this.socket.on('disconnect', () => {
      console.log('Disconnected from WebSocket server');
      this.connected = false;
      this.notifyListeners('connection', { connected: false });
    });

    this.socket.on('connect_error', (error) => {
      console.error('Connection error:', error);
      this.connected = false;
    });

    // Proposal events
    this.socket.on('proposal-received', (data) => {
      this.notifyListeners('proposal-received', data);
    });

    this.socket.on('proposal-status-updated', (data) => {
      this.notifyListeners('proposal-status-updated', data);
    });

    this.socket.on('proposal-update', (data) => {
      this.notifyListeners('proposal-update', data);
    });

    // Message events
    this.socket.on('message-received', (data) => {
      this.notifyListeners('message-received', data);
    });

    // Typing events
    this.socket.on('user-typing', (data) => {
      this.notifyListeners('user-typing', data);
    });

    this.socket.on('user-stopped-typing', (data) => {
      this.notifyListeners('user-stopped-typing', data);
    });

    // Project events
    this.socket.on('project-updated', (data) => {
      this.notifyListeners('project-updated', data);
    });

    this.socket.on('deliverable-uploaded', (data) => {
      this.notifyListeners('deliverable-uploaded', data);
    });

    this.socket.on('payment-updated', (data) => {
      this.notifyListeners('payment-updated', data);
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
    }
  }

  // Room management
  joinProject(projectId) {
    if (this.socket && this.connected) {
      this.socket.emit('join-project', projectId);
    }
  }

  leaveProject(projectId) {
    if (this.socket && this.connected) {
      this.socket.emit('leave-project', projectId);
    }
  }

  // Event emitters
  sendProposal(data) {
    if (this.socket && this.connected) {
      this.socket.emit('new-proposal', data);
    }
  }

  updateProposalStatus(data) {
    if (this.socket && this.connected) {
      this.socket.emit('proposal-status-change', data);
    }
  }

  sendMessage(data) {
    if (this.socket && this.connected) {
      this.socket.emit('send-message', data);
    }
  }

  startTyping(projectId) {
    if (this.socket && this.connected) {
      this.socket.emit('typing', { projectId });
    }
  }

  stopTyping(projectId) {
    if (this.socket && this.connected) {
      this.socket.emit('stop-typing', { projectId });
    }
  }

  // Event listener management
  addEventListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  removeEventListener(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  notifyListeners(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }
  }

  // Get connection status
  isConnected() {
    return this.connected;
  }
}

// Create singleton instance
const socketService = new SocketService();

export default socketService;
